import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GRADEBOOK_SORT } from '../../../../utils/constants';

// Helper: Check if a column ID represents the name column
function isNameColumn(columnId) {
  return columnId === 'name' || columnId === GRADEBOOK_SORT.NAME_SORT_COLUMN;
}

// Helper: Check if a column ID represents course total
function isCourseTotalColumn(columnId) {
  return columnId === 'courseTotal';
}

// Helper: Check if a column ID represents a category total
function isCategoryTotalColumn(columnId) {
  return columnId?.startsWith('category-total-');
}

// Helper: Normalize direction to 'asc' or 'desc'
function normalizeDirection(direction) {
  if (direction === 'asc' || direction === GRADEBOOK_SORT.LODASH_ASCENDING) {
    return 'asc';
  }
  if (direction === 'desc' || direction === GRADEBOOK_SORT.LODASH_DESCENDING) {
    return 'desc';
  }
  return direction;
}

/**
 * Convert sortColumn to URL param
 * V1 uses null for name sort, 'course_total' for course total, category ID for category, assignment ID for assignment
 */
function sortColumnToUrlParam(sortColumn) {
  if (!sortColumn || sortColumn === 'name') {
    return null;
  }
  if (sortColumn === 'courseTotal') {
    return 'course_total';
  }
  if (sortColumn.startsWith('category-total-')) {
    return sortColumn.replace('category-total-', '');
  }
  return sortColumn;
}

/**
 * Convert URL param to sortColumn
 * Need categories to distinguish between category IDs and assignment IDs
 */
function urlParamToSortColumn(studentSortKey, categories = []) {
  if (!studentSortKey) {
    return 'name';
  }
  if (studentSortKey === 'course_total') {
    return 'courseTotal';
  }
  const categoryIds = categories.map((c) => String(c.id));
  if (categoryIds.includes(String(studentSortKey))) {
    return `category-total-${studentSortKey}`;
  }
  return studentSortKey;
}

/**
 * Update URL with sort parameters
 */
function updateSortParamsInUrl(sortColumn, sortDirection) {
  const searchParams = new URLSearchParams(window.location.search);
  const studentSortKey = sortColumnToUrlParam(sortColumn);
  const direction = sortDirection || 'asc';

  searchParams.set('studentSortDirection', direction);
  searchParams.set('iconSortDirection', direction);
  if (studentSortKey) {
    searchParams.set('studentSortKey', studentSortKey);
  } else {
    searchParams.delete('studentSortKey');
  }

  const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
  window.history.pushState(null, '', newRelativePathQuery);
}

/**
 * Hook for managing gradebook sorting state
 * Supports both client-side sorting (local state) and server-side sorting (via Redux)
 *
 * @param {Object} options
 * @param {Function} options.dispatch - Redux dispatch function (optional)
 * @param {Function} options.getGradebook - Action creator for fetching gradebook (optional, legacy)
 * @param {Function} options.getGradebookInstructor - Action creator for new consolidated endpoint (optional)
 * @param {Function} options.updateGradebook - Action creator for updating gradebook state (optional)
 * @param {string} options.courseId - Course ID for server-side sorting (optional)
 * @param {string} options.reduxSortColumn - Current sort column from Redux state (optional)
 * @param {string} options.reduxSortDirection - Current sort direction from Redux state (optional)
 * @param {number} options.count - Total number of students (optional)
 * @param {number} options.limit - Current page size (optional)
 * @param {string} options.searchPhrase - Current search filter (optional)
 * @param {Array} options.exceptionGroups - Current exception group filter (optional)
 * @param {Array} options.categories - Categories for mapping URL params (optional)
 * @param {boolean} options.showPercent - Whether to show/sort by percentages (true) or points (false)
 */
export function useGradebookSorting(options = {}) {
  const {
    dispatch,
    getGradebook,
    getGradebookInstructor,
    updateGradebook,
    courseId,
    reduxSortColumn,
    reduxSortDirection,
    count,
    limit,
    searchPhrase,
    exceptionGroups,
    categories = [],
    showPercent = true,
  } = options;

  // Local state for client-side sorting (fallback when Redux not available)
  const [localSortBy, setLocalSortBy] = useState(null);
  const [localSortOrder, setLocalSortOrder] = useState(null);

  // Use Redux state if available, otherwise local state
  const sortBy = reduxSortColumn ?? localSortBy;
  const sortOrder = reduxSortDirection ?? localSortOrder;

  // Normalize the effective sort column (handle NAME_SORT_COLUMN vs 'name')
  const effectiveSortColumn = isNameColumn(reduxSortColumn)
    ? 'name'
    : reduxSortColumn || 'name';
  const effectiveSortDirection = reduxSortDirection || 'asc';

  // Initialize URL params on mount if not present (matches V1 behavior)
  useEffect(
    () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hasUrlSortParams =
        searchParams.has('studentSortDirection') ||
        searchParams.has('iconSortDirection');

      if (!hasUrlSortParams) {
        // Initialize URL with current sort state
        updateSortParamsInUrl(effectiveSortColumn, effectiveSortDirection);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Sync sort state from URL params to Redux
  // Idempotent: only dispatches when URL differs from Redux state
  useEffect(() => {
    if (!dispatch || !updateGradebook) return;

    const searchParams = new URLSearchParams(window.location.search);
    const studentSortKey = searchParams.get('studentSortKey') || null;
    const urlSortDirection =
      searchParams.get('studentSortDirection') ||
      searchParams.get('iconSortDirection') ||
      null;

    if (!urlSortDirection) return;

    const urlSortColumn = urlParamToSortColumn(studentSortKey, categories);

    if (
      urlSortColumn !== effectiveSortColumn ||
      urlSortDirection !== effectiveSortDirection
    ) {
      dispatch(
        updateGradebook({
          sortColumn: urlSortColumn,
          sortDirection: urlSortDirection,
        }),
      );
    }
  }, [
    dispatch,
    updateGradebook,
    categories,
    effectiveSortColumn,
    effectiveSortDirection,
  ]);

  // Refetch when showPercent changes and we're sorted by course_total or category
  // This ensures the sort order is correct for the current display mode (points vs percent)
  const prevShowPercentRef = useRef(showPercent);
  useEffect(() => {
    // Skip on initial render
    if (prevShowPercentRef.current === showPercent) {
      return;
    }
    prevShowPercentRef.current = showPercent;

    // Only refetch if we're sorted by something that depends on points vs percent
    const needsRefetch =
      isCourseTotalColumn(effectiveSortColumn) ||
      isCategoryTotalColumn(effectiveSortColumn);

    if (needsRefetch && dispatch && getGradebookInstructor && courseId) {
      const baseOptions = {
        page: 1,
        pageSize: limit || 25,
        sortDirection: effectiveSortDirection,
        search: searchPhrase || '',
        exceptionGroupIds: (exceptionGroups || []).map((g) => g.groupId),
        sortByPoints: !showPercent,
      };

      if (isCourseTotalColumn(effectiveSortColumn)) {
        dispatch(
          getGradebookInstructor(courseId, {
            ...baseOptions,
            sortBy: 'course_total',
          }),
        );
      } else {
        const categoryId = effectiveSortColumn.replace('category-total-', '');
        dispatch(
          getGradebookInstructor(courseId, {
            ...baseOptions,
            sortByCategory: parseInt(categoryId, 10),
          }),
        );
      }
    }
  }, [
    showPercent,
    effectiveSortColumn,
    effectiveSortDirection,
    dispatch,
    getGradebookInstructor,
    courseId,
    limit,
    searchPhrase,
    exceptionGroups,
  ]);

  // Get current sorting state
  const sortingState = useMemo(() => {
    return {
      columnId: sortBy,
      direction: sortOrder,
    };
  }, [sortBy, sortOrder]);

  // Update sorting - supports both client-side and server-side
  const handleSort = useCallback(
    (columnId) => {
      // Determine if clicking the same column
      const clickingName = isNameColumn(columnId);
      const currentIsName = isNameColumn(sortBy);
      const isSameColumn = clickingName ? currentIsName : sortBy === columnId;

      // Get effective current direction (default to 'asc' for initial name sort)
      const currentDir = normalizeDirection(
        sortOrder || (currentIsName ? 'asc' : null),
      );

      // Toggle: asc -> desc -> reset to name asc (always keep one column sorted)
      let newColumn;
      let newDirection;

      if (!isSameColumn) {
        newColumn = columnId;
        newDirection = 'asc';
      } else if (currentDir === 'asc') {
        newColumn = columnId;
        newDirection = 'desc';
      } else if (currentDir === 'desc') {
        // Reset to default name sort
        newColumn = 'name';
        newDirection = 'asc';
      } else {
        newColumn = columnId;
        newDirection = 'asc';
      }

      // If Redux dispatch is available, use server-side sorting
      if (dispatch && (getGradebookInstructor || getGradebook) && courseId) {
        // Determine sort type (check newColumn to handle reset-to-default case)
        const sortingName = isNameColumn(newColumn) || isNameColumn(columnId);
        const sortingCourseTotal =
          isCourseTotalColumn(columnId) && isCourseTotalColumn(newColumn);
        const sortingCategory =
          isCategoryTotalColumn(columnId) && newColumn === columnId;

        // Use the new consolidated endpoint if available
        if (getGradebookInstructor) {
          const baseOptions = {
            page: 1,
            pageSize: limit || 25,
            sortDirection: newDirection,
            search: searchPhrase || '',
            exceptionGroupIds: (exceptionGroups || []).map((g) => g.groupId),
          };

          if (sortingName) {
            dispatch(
              getGradebookInstructor(courseId, {
                ...baseOptions,
                sortBy: 'name',
              }),
            );
          } else if (sortingCourseTotal) {
            dispatch(
              getGradebookInstructor(courseId, {
                ...baseOptions,
                sortBy: 'course_total',
                sortByPoints: !showPercent,
              }),
            );
          } else if (sortingCategory) {
            const categoryId = columnId.replace('category-total-', '');
            dispatch(
              getGradebookInstructor(courseId, {
                ...baseOptions,
                sortByCategory: parseInt(categoryId, 10),
                sortByPoints: !showPercent,
              }),
            );
          } else {
            // Assignment column sorting
            dispatch(
              getGradebookInstructor(courseId, {
                ...baseOptions,
                sortByAssignment: newColumn,
              }),
            );
          }
        } else if (getGradebook) {
          // Fallback to legacy endpoint
          const legacyOptions = {
            studentSortDirection: newDirection,
            iconSortDirection: newDirection,
            page: 1,
          };

          if (sortingName) {
            dispatch(
              getGradebook(courseId, {
                ...legacyOptions,
                studentSortKey: null,
              }),
            );
          } else if (sortingCourseTotal) {
            dispatch(
              getGradebook(courseId, {
                ...legacyOptions,
                studentSortKey: 'course_total',
                limit: count,
              }),
            );
          } else if (sortingCategory) {
            const categoryId = columnId.replace('category-total-', '');
            dispatch(
              getGradebook(courseId, {
                ...legacyOptions,
                studentSortKey: categoryId,
                limit: count,
              }),
            );
          } else {
            // Assignment column sorting
            dispatch(
              getGradebook(courseId, {
                ...legacyOptions,
                studentSortKey: newColumn,
                sortByGrade: true,
                limit: count,
              }),
            );
          }
        }

        // Update local UI state via Redux
        if (updateGradebook) {
          dispatch(
            updateGradebook({
              page: 1,
              sortColumn: newColumn,
              sortDirection: newDirection,
            }),
          );
        }

        updateSortParamsInUrl(newColumn, newDirection);
      } else {
        // Fallback to local state for client-side sorting
        setLocalSortBy(newColumn);
        setLocalSortOrder(newDirection);
        updateSortParamsInUrl(newColumn, newDirection);
      }
    },
    [
      sortBy,
      sortOrder,
      dispatch,
      getGradebook,
      getGradebookInstructor,
      updateGradebook,
      courseId,
      count,
      limit,
      searchPhrase,
      exceptionGroups,
      showPercent,
    ],
  );

  // Reset to default name sort
  const clearSort = useCallback(() => {
    if (dispatch && updateGradebook) {
      dispatch(updateGradebook({ sortColumn: 'name', sortDirection: 'asc' }));
    }
    setLocalSortBy('name');
    setLocalSortOrder('asc');
    updateSortParamsInUrl('name', 'asc');
  }, [dispatch, updateGradebook]);

  // Get sort state for a specific column
  const getColumnSortState = useCallback(
    (columnId) => {
      const { columnId: currentColumnId, direction: currentDirection } =
        sortingState;

      // Check if this column is the sorted column
      const currentIsName = isNameColumn(currentColumnId);
      const isMatch = isNameColumn(columnId)
        ? currentIsName
        : currentColumnId === columnId;

      if (!isMatch) {
        return false;
      }

      // Default to 'asc' for name column when direction not set
      const dir = currentDirection || (currentIsName ? 'asc' : null);
      return normalizeDirection(dir) || false;
    },
    [sortingState],
  );

  // Get aria-sort attribute value for accessibility
  const getAriaSortValue = useCallback(
    (columnId) => {
      const sortState = getColumnSortState(columnId);
      if (sortState === 'asc') return 'ascending';
      if (sortState === 'desc') return 'descending';
      return 'none';
    },
    [getColumnSortState],
  );

  return {
    sortingState,
    handleSort,
    clearSort,
    getColumnSortState,
    getAriaSortValue,
  };
}
